import React, { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "../hooks";
import { fetchWeather } from "../api/weather";
import {
  WiCloud,
  WiDaySunny,
  WiDust,
  WiNa,
  WiNightClear,
  WiRain,
  WiSnow,
  WiThunderstorm,
} from "react-icons/wi";

export const WeatherContext = React.createContext({});

/**
 * The weather context provider
 * 
 * @param {Object} props The props of the component
 * @returns The weather context provider
 */
const WeatherProvider = ({ children }) => {
  const { currentLatitude, currentLongitude } = useLocation();

  /**
   * Wraps the weather query function
   * 
   * @returns The weather query function
   */
  const weatherQueryFunction = useCallback(
    () =>
      fetchWeather({
        latitude: currentLatitude,
        longitude: currentLongitude,
        apiKey: process.env.REACT_APP_OPEN_WEATHER_API_KEY,
      }),
    [currentLatitude, currentLongitude],
  );

  const weatherQuery = useQuery({
    queryKey: ["weather"],
    queryFn: weatherQueryFunction,
    enabled: !!currentLatitude && !!currentLongitude,
  });

  const weather = useMemo(() => weatherQuery.data, [weatherQuery.data]);

  const temperature = useMemo(() => weather?.main?.temp, [weather?.main?.temp]);

  const icon = useMemo(() => {
    const weatherCode = weather?.weather?.[0]?.id;
    const weatherMain = weather?.weather?.[0]?.main;
    const isDay = weather?.weather?.[0]?.icon?.[2] === "d";

    if (weatherMain === "Clouds") {
      return WiCloud;
    } else if (weatherMain === "Clear") {
      return isDay ? WiDaySunny : WiNightClear;
    } else if (weatherCode >= 700) {
      return WiDust;
    } else if (weatherMain === "Snow") {
      return WiSnow;
    } else if (weatherMain === "Rain" || weatherMain === "Drizzle") {
      return WiRain;
    } else if (weatherMain === "Thunderstorm") {
      return WiThunderstorm;
    }
    return WiNa;
  }, [weather?.weather]);

  const contextValue = useMemo(
    () => ({
      icon,
      temperature,
      weather,
    }),
    [icon, temperature, weather],
  );

  return (
    <WeatherContext.Provider value={contextValue}>
      {children}
    </WeatherContext.Provider>
  );
};

export default React.memo(WeatherProvider);
